import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Users, Building2, Calendar, Briefcase, FileText, TrendingUp } from 'lucide-react';

const HRMSDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    departments: 0,
    presentToday: 0,
    onLeave: 0,
    pendingLeaves: 0,
    upcomingHolidays: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Employees',
      value: stats.totalEmployees,
      icon: Users,
      color: 'bg-[#2b338015]0',
      textColor: 'text-[#2b3380]'
    },
    {
      title: 'Departments',
      value: stats.departments,
      icon: Building2,
      color: 'bg-purple-500',
      textColor: 'text-purple-600'
    },
    {
      title: 'Present Today',
      value: stats.presentToday,
      icon: Calendar,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'On Leave',
      value: stats.onLeave,
      icon: Briefcase,
      color: 'bg-orange-500',
      textColor: 'text-orange-600'
    },
    {
      title: 'Pending Leave Requests',
      value: stats.pendingLeaves,
      icon: FileText,
      color: 'bg-red-500',
      textColor: 'text-red-600'
    },
    {
      title: 'Upcoming Holidays',
      value: stats.upcomingHolidays,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600'
    }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-gray-900">HRMS Dashboard</h1>
          <p className="text-gray-600 mt-2">Comprehensive Human Resource Management System Overview</p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statCards.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-full`}>
                    <stat.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/employees"
              className="flex flex-col items-center p-4 rounded-lg bg-[#2b338015] hover:bg-[#2b338030] transition-colors"
            >
              <Users className="w-8 h-8 text-[#2b3380] mb-2" />
              <span className="text-sm font-medium text-gray-700">Employees</span>
            </a>
            <a
              href="/attendance"
              className="flex flex-col items-center p-4 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
            >
              <Calendar className="w-8 h-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Attendance</span>
            </a>
            <a
              href="/leaves"
              className="flex flex-col items-center p-4 rounded-lg bg-orange-50 hover:bg-orange-100 transition-colors"
            >
              <Briefcase className="w-8 h-8 text-orange-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Leaves</span>
            </a>
            <a
              href="/payroll"
              className="flex flex-col items-center p-4 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors"
            >
              <FileText className="w-8 h-8 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Payroll</span>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HRMSDashboard;
